import React, { useEffect, useMemo, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { addDays, min as dateMin, max as dateMax } from "date-fns";
import { ptBR } from "date-fns/locale/pt-BR";

import "react-datepicker/dist/react-datepicker.min.css";
import "./analytics.css";
import "./analytics-datepicker.css";

registerLocale("pt-BR", ptBR);

const PRESET_TODAY = "today";
const PRESET_7 = "7d";
const PRESET_15 = "15d";
const PRESET_30 = "30d";
const PRESET_60 = "60d";
const PRESET_CUSTOM = "custom";

const MAX_RANGE_MS = 62 * 24 * 60 * 60 * 1000;

function startOfDay(d) {
	const x = new Date(d);
	x.setHours(0, 0, 0, 0);
	return x;
}

function endOfDay(d) {
	const x = new Date(d);
	x.setHours(23, 59, 59, 999);
	return x;
}

/** Dados de analytics existem a partir desta data (inclusive). */
const ANALYTICS_MIN_DATE = startOfDay(new Date(2026, 0, 1));

function rangeFromPreset(preset) {
	const end = endOfDay(new Date());
	let start;

	switch (preset) {
		case PRESET_TODAY:
			start = startOfDay(new Date());
			break;
		case PRESET_7: {
			const s = new Date();
			s.setDate(s.getDate() - 6);
			start = startOfDay(s);
			break;
		}
		case PRESET_15: {
			const s = new Date();
			s.setDate(s.getDate() - 14);
			start = startOfDay(s);
			break;
		}
		case PRESET_30: {
			const s = new Date();
			s.setDate(s.getDate() - 29);
			start = startOfDay(s);
			break;
		}
		case PRESET_60: {
			const s = new Date();
			s.setDate(s.getDate() - 59);
			start = startOfDay(s);
			break;
		}
		default:
			start = startOfDay(new Date());
	}

	start = dateMax([start, ANALYTICS_MIN_DATE]);
	if (start > end) {
		start = dateMax([startOfDay(end), ANALYTICS_MIN_DATE]);
	}
	return { start, end };
}

function clampCustomRange(start, end) {
	const s = dateMax([startOfDay(start), ANALYTICS_MIN_DATE]);
	let e = endOfDay(end);
	const todayEnd = endOfDay(new Date());
	const maxByWindow = endOfDay(addDays(s, 62));
	const cap = dateMin([maxByWindow, todayEnd]);
	if (e > cap) e = cap;
	if (e < s) e = endOfDay(s);
	return { start: s, end: e };
}

const formatConversationCount = (n) =>
	new Intl.NumberFormat("pt-BR").format(typeof n === "number" ? n : 0);

/**
 * @param {{
 *   onRangeChange: (iso: { startDate: string, endDate: string }) => void;
 *   conversationCount?: number;
 *   conversationsReady?: boolean;
 *   conversationsRefreshing?: boolean;
 * }} props
 */
export default function DateRangeFilter({
	onRangeChange,
	conversationCount = 0,
	conversationsReady = false,
	conversationsRefreshing = false,
}) {
	const [preset, setPreset] = useState(PRESET_7);
	const [rangeStart, setRangeStart] = useState(() => {
		const s = new Date();
		s.setDate(s.getDate() - 6);
		return dateMax([startOfDay(s), ANALYTICS_MIN_DATE]);
	});
	const [rangeEnd, setRangeEnd] = useState(() => endOfDay(new Date()));
	const [error, setError] = useState("");

	const todayEnd = useMemo(() => endOfDay(new Date()), []);

	const onCustomRangeChange = (update) => {
		const [s, e] = update;
		if (!s) {
			setRangeStart(null);
			setRangeEnd(null);
			return;
		}
		const s0 = dateMax([startOfDay(s), ANALYTICS_MIN_DATE]);
		if (!e) {
			setRangeStart(s0);
			setRangeEnd(null);
			return;
		}
		const { start: cs, end: ce } = clampCustomRange(s0, e);
		setRangeStart(cs);
		setRangeEnd(ce);
	};

	useEffect(() => {
		if (preset !== PRESET_CUSTOM) {
			const { start, end } = rangeFromPreset(preset);
			setError("");
			onRangeChange({
				startDate: start.toISOString(),
				endDate: end.toISOString(),
			});
			return;
		}

		if (!rangeStart || !rangeEnd) {
			setError("");
			return;
		}

		const start = dateMax([startOfDay(rangeStart), ANALYTICS_MIN_DATE]);
		const end = endOfDay(rangeEnd);
		if (start > end) {
			setError("A data inicial deve ser anterior à final.");
			return;
		}
		if (end.getTime() - start.getTime() > MAX_RANGE_MS) {
			setError("O período máximo permitido é de 2 meses.");
			return;
		}
		setError("");
		onRangeChange({
			startDate: start.toISOString(),
			endDate: end.toISOString(),
		});
	}, [preset, rangeStart, rangeEnd, onRangeChange]);

	const selectOptions = useMemo(
		() => [
			{ value: PRESET_TODAY, label: "Hoje" },
			{ value: PRESET_7, label: "Últimos 7 dias" },
			{ value: PRESET_15, label: "Últimos 15 dias" },
			{ value: PRESET_30, label: "Últimos 30 dias" },
			{ value: PRESET_60, label: "Últimos 60 dias" },
			{ value: PRESET_CUSTOM, label: "Período personalizado" },
		],
		[]
	);

	return (
		<div className="analytics-toolbar">
			<span className="analytics-toolbar__label">Filtrar por</span>
			<select
				className="analytics-toolbar__select"
				value={preset}
				onChange={(e) => setPreset(e.target.value)}
				aria-label="Período por data de criação da conversa"
			>
				{selectOptions.map((o) => (
					<option key={o.value} value={o.value}>
						{o.label}
					</option>
				))}
			</select>
			<div
				className={`analytics-toolbar__total${conversationsRefreshing ? " analytics-toolbar__total--refreshing" : ""}`}
				aria-live="polite"
				aria-busy={conversationsRefreshing}
			>
				<span className="analytics-toolbar__total-label">Conversas criadas no período</span>
				<span className="analytics-toolbar__total-value">
					{conversationsReady
						? formatConversationCount(conversationCount)
						: "…"}
				</span>
			</div>
			{preset === PRESET_CUSTOM ? (
				<div className="analytics-toolbar__dates analytics-toolbar__dates--picker">
					<div className="analytics-datepicker-field analytics-datepicker-field--range">
						<span id="analytics-date-range-label">Período</span>
						<DatePicker
							selectsRange
							startDate={rangeStart}
							endDate={rangeEnd}
							onChange={onCustomRangeChange}
							dateFormat="dd/MM/yyyy"
							locale="pt-BR"
							minDate={ANALYTICS_MIN_DATE}
							maxDate={todayEnd}
							showIcon
							toggleCalendarOnIconClick
							isClearable={false}
							showPopperArrow={false}
							popperClassName="analytics-datepicker-popper"
							calendarClassName="analytics-datepicker-calendar"
							className="analytics-datepicker-input analytics-datepicker-input--range"
							placeholderText="Clique para escolher as datas"
							ariaLabelledBy="analytics-date-range-label"
						/>
					</div>
				</div>
			) : null}
			{error ? (
				<span className="analytics-toolbar__error" role="alert">
					{error}
				</span>
			) : null}
		</div>
	);
}
